import { format, parseISO } from 'date-fns';
import { z } from 'zod';
import {
  OrderStatus,
  type Order,
  type CustomerSelf as Customer,
} from '@hungryroot/rest';
export { useLogUser } from './hooks/useLogUser';
export { RequireCheckedOut } from './components/RequireCheckedOut/RequireCheckedOut';
export { RequireLoggedIn } from './components/RequireLoggedIn/RequireLoggedIn';
export { RequireNotCheckedOut } from './components/RequireNotCheckedOut/RequireNotCheckedOut';
export { AuthGuard } from './components/AuthGuard/AuthGuard';
export { getCustomerDisplayName } from './getCustomerDisplayName';
export type { Customer };

export const LoginSchema = z.object({
  email: z
    .string({ message: 'Email Address is a required field' })
    .email({ message: 'Invalid email address' }),
  password: z.string({ message: 'Password is a required field' }),
});

export enum AccountStatus {
  Active = 'active',
  Canceled = 'canceled',
  Paused = 'paused',
  Skipped = 'skipped',
}

export function getAccountStatus(
  customer: Pick<
    Customer,
    'plan' | 'is_on_hold' | 'on_hold_until_date' | 'is_cancelled'
  >,
  nextCustomizableOrder?:
    | Pick<Order, 'status' | 'days_left_to_customize'>
    | undefined
): {
  accountStatus: AccountStatus;
  nextDeliveryDate?: string;
  daysToCustomize?: number;
} {
  const isCanceled = customer.is_cancelled;
  if (isCanceled) {
    return { accountStatus: AccountStatus.Canceled };
  }

  const isPaused = customer.is_on_hold;
  const isSkipped = nextCustomizableOrder?.status === OrderStatus.Skipped;

  if (isPaused) {
    return {
      accountStatus: AccountStatus.Paused,
      nextDeliveryDate:
        customer.on_hold_until_date !== null
          ? format(parseISO(customer.on_hold_until_date), 'MMMM d')
          : undefined,
      daysToCustomize: undefined,
    };
  } else if (isSkipped) {
    return {
      accountStatus: AccountStatus.Skipped,
      nextDeliveryDate: undefined,
      daysToCustomize:
        nextCustomizableOrder.days_left_to_customize || undefined,
    };
  }

  return {
    accountStatus: AccountStatus.Active,
    nextDeliveryDate: undefined,
    daysToCustomize: undefined,
  };
}
